import * as Sentry from "@sentry/react";
import { FC, RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Rnd } from "react-rnd";

import {
  facilitiesLegendItemsAtom,
  facilitiesLegendTitleAtom,
  wellsLegendItemsAtom,
  wellsLegendTitleAtom
} from "atoms";
import { DEFAULT_FONT_SIZE } from "constants/chart.constants";
import { useAtomValue } from "jotai";
import { RootState } from "store/rootReducer";
import styled from "styled-components/macro";
import { isScrollbarClicked } from "utils";

import { useLocalStorage } from "hooks";

import { MapLegendItem } from "api/map";

import { LegendItemModel } from "models/LegendItem";
import { EntityKind } from "models/entityKind";

import { LEGEND_USAGE_TYPES } from "components/base";
import { useMapContext } from "components/map/hooks/useMapContext";
import { useScreenshotContext } from "components/screenshot/hooks";
import { applyAbbreviations } from "components/screenshot/utils";
import {
  LegendDimensionsT,
  LegendPositionT,
  LegendT,
  ParentDimensionsT
} from "components/shared/charts/legend/Legend";
import getCalculatedLegend from "components/shared/charts/legend/utils/getCalculatedLegend";
import {
  LegendPosition,
  getPositionFromChartSettings
} from "components/shared/charts/legend/utils/getPositionFromChartSettings";
import { Legend } from "components/ui";
import { useUserSettings } from "components/user/hooks";

import { useScreenshotFrameCounts } from "../hooks/useScreenshotFrameCounts";
import { applyFrameCounts } from "../utils/applyFrameCounts";
import { LEGEND_WRAPPER_ITEMS_PADDING } from "./legendWrapper.constants";

/**
 * How many group names each side contributes to a key-mismatch report. Enough to
 * see how the two sides differ without putting a whole legend in the event.
 */
const MAX_REPORTED_GROUP_KEYS = 5;

/**
 * How long a total miss must stand before it is reported. The legend rows and
 * the map's groups arrive from separate requests, so changing the group-by
 * field mid-composition opens a brief window where the rows already belong to
 * the new field and the groups still to the old one. That heals when the new
 * groups land — one request round-trip — while a keying bug stands
 * indefinitely. Armed as a timer rather than counted across recounts: an
 * unchanged count keeps its object identity, so a static frame may never
 * produce the second judgement a recount-based clock would need.
 */
export const MISMATCH_REPORT_AFTER_MS = 3000;

/**
 * How long the frame must stay empty before the legend says so. A count can
 * transiently read all-zeros while the tile cache swaps under a refilter; one
 * such artifact must not put the message into a capture. A genuinely empty
 * frame is stable, so the message just lands a beat later. A capture clicked
 * inside this window still gets the blank box — accepted over flashing a
 * false message into captures.
 */
export const EMPTY_FRAME_SETTLE_MS = 500;

/**
 * One unmatched row can be legitimate; no row matching at all cannot. It means
 * the two sides are keyed differently, and the per-row fallback then quietly
 * prints the whole map's counts on every row of the screenshot. That fallback
 * is invisible by design, so the total miss has to announce itself.
 */
const reportGroupKeyMismatch = (
  entityKind: EntityKind,
  legendItems: MapLegendItem[],
  frameCountsByGroup: Record<string, number>
): void => {
  Sentry.withScope((scope) => {
    scope.setContext("screenshotFrameLegend", {
      entityKind,
      legendRowCount: legendItems.length,
      // The two key sets side by side are the whole diagnosis.
      legendItemTitles: legendItems
        .slice(0, MAX_REPORTED_GROUP_KEYS)
        .map((item) => item.title),
      frameCountGroupKeys: Object.keys(frameCountsByGroup).slice(
        0,
        MAX_REPORTED_GROUP_KEYS
      )
    });
    scope.setLevel("error");
    Sentry.captureMessage(
      "Screenshot frame legend matched no group; counts fell back to the whole map"
    );
  });
};

const LegendWrapper: FC<{
  width: number;
  parentDimensions: ParentDimensionsT;
  isScreenshotOverlayVisible: boolean;
  /**
   * True only when the map's capture frame is on screen. Distinct from
   * `isScreenshotOverlayVisible`, which the dashboard legends hardcode to get
   * screenshot *styling* — keying the frame-scoped counts on that would count
   * every dashboard legend against the whole map container.
   */
  isCaptureFrameVisible?: boolean;
  /** The capture frame element the counts are scoped to; the one the capture crops to. */
  captureFrameRef?: RefObject<HTMLDivElement>;
  entityKind?: EntityKind;
  defaultPosition?: LegendPosition;
}> = ({
  width = 200,
  parentDimensions = undefined,
  isScreenshotOverlayVisible,
  isCaptureFrameVisible = false,
  captureFrameRef = undefined,
  entityKind = EntityKind.Well,
  defaultPosition = LegendPosition.NE
}) => {
  // Dynamic storage keys based on entity kind
  const dimensionsStorageKey = `${
    entityKind === EntityKind.Facility ? "midstream-" : ""
  }map-legend-dimensions`;
  const positionStorageKey = `${
    entityKind === EntityKind.Facility ? "midstream-" : ""
  }map-legend-position`;
  // Hooks
  const { mapbox, entitiesInGroup, facilitiesInGroup } = useMapContext();
  const { settings } = useScreenshotContext();

  const { getStoredValue: getStoredDimensions } = useLocalStorage(
    dimensionsStorageKey,
    undefined
  );

  const { getStoredValue: getStoredPosition } = useLocalStorage(
    positionStorageKey,
    undefined
  );
  const { userAbbreviations } = useUserSettings();

  const rndRef = useRef<Rnd>(null);
  const missReportTimerRef = useRef<Nullable<ReturnType<typeof setTimeout>>>(null);
  const standingMissRef = useRef<
    Nullable<{
      /** The row titles the miss was judged against — a re-key restarts the clock. */
      rowKeys: string;
      /** The count's group keys — these re-key when the map's groups land. */
      countKeys: string;
      legendItems: MapLegendItem[];
      frameCountsByGroup: Record<string, number>;
    }>
  >(null);
  const emptyFrameTimerRef = useRef<Nullable<ReturnType<typeof setTimeout>>>(null);
  const hasReportedGroupKeyMismatchRef = useRef(false);

  const clearStandingMiss = useCallback(() => {
    standingMissRef.current = null;
    if (missReportTimerRef.current !== null) {
      clearTimeout(missReportTimerRef.current);
      missReportTimerRef.current = null;
    }
  }, []);

  const wellsLegendItems = useAtomValue(wellsLegendItemsAtom);
  const facilitiesLegendItems = useAtomValue(facilitiesLegendItemsAtom);
  const wellLegendTitle = useAtomValue(wellsLegendTitleAtom);
  const facilityLegendTitle = useAtomValue(facilitiesLegendTitleAtom);

  const activeLegendItems =
    entityKind === EntityKind.Facility ? facilitiesLegendItems : wellsLegendItems;

  const activeTitle =
    entityKind === EntityKind.Facility ? facilityLegendTitle : wellLegendTitle;

  const propertiesFilter = useSelector(
    (state: RootState) => state.filter.propertiesFilter
  );
  const facilitiesPropertiesFilter = useSelector(
    (state: RootState) => state.facilityFilter.propertiesFilter
  );
  const showGroupsNotInFilter = useSelector(
    (state: RootState) => state.filter.showGroupsNotInFilter
  );
  const facilitiesShowGroupsNotInFilter = useSelector(
    (state: RootState) => state.facilityFilter.showGroupsNotInFilter
  );
  const placeholderBinsEnabled = useSelector(
    (state: RootState) => state.groupBy.placeholderBinsEnabled
  );

  const [groupLegendItems, setGroupLegendItems] = useState([]);
  const [isFrameEmpty, setIsFrameEmpty] = useState(false);

  const [dimensions, setDimensions] = useState<LegendDimensionsT>(() => {
    // Apply change when implementing legendFontSize
    // const itemSize = screenshot.visible ? legendFontSize + 12 : 30; // 30px for chart, 12px gap around when taking screenshot
    const itemSize = 12; // 30px for chart, 12px gap around when taking screenshot

    const itemsHeight =
      Array.isArray(activeLegendItems) && activeLegendItems.length
        ? activeLegendItems.length * itemSize
        : 0;

    const storedDimensions = getStoredDimensions();
    return storedDimensions ? storedDimensions : { width, height: itemsHeight + 32 }; // 32px for title
  });

  const [position, setPosition] = useState<LegendPositionT>(() => {
    const storedPosition = getStoredPosition();
    return storedPosition
      ? storedPosition
      : getPositionFromChartSettings(dimensions, parentDimensions, defaultPosition);
  });

  // Whether this legend is switched on for screenshots at all.
  const shouldShow =
    entityKind === EntityKind.Facility
      ? settings?.showMidstreamLegendInScreenshot
      : settings?.showLegendInScreenshot;
  const isLegendHiddenBySettings = Boolean(settings) && !shouldShow;

  // How many painted entities fall inside the capture frame, per legend group.
  // Null whenever the live counts should be shown as-is.
  const activeEntitiesByGroup =
    entityKind === EntityKind.Facility ? facilitiesInGroup : entitiesInGroup;

  const frameCountsByGroup = useScreenshotFrameCounts({
    mapbox,
    entityKind,
    isComposing: isCaptureFrameVisible && !isLegendHiddenBySettings,
    frameRef: captureFrameRef,
    entitiesByGroup: activeEntitiesByGroup
  });
  const legendTitleSize = isScreenshotOverlayVisible
    ? Number(settings?.legendTitleSize ?? DEFAULT_FONT_SIZE) *
      (Number(settings?.mapScreenshotScale) ?? 1)
    : DEFAULT_FONT_SIZE;
  const legendFontSize = isScreenshotOverlayVisible
    ? Number(settings?.legendFontSize ?? DEFAULT_FONT_SIZE) *
      (Number(settings?.mapScreenshotScale) ?? 1)
    : DEFAULT_FONT_SIZE;
  const mapScreenshotScale =
    Number(settings?.mapScreenshotScale) < 1 && isScreenshotOverlayVisible
      ? Number(settings?.mapScreenshotScale)
      : 1;

  const updateGroupLegendItems = useCallback(() => {
    if (!isScreenshotOverlayVisible) return;

    const sourceLegend = {
      legendItems: activeLegendItems,
      title: activeTitle
    };

    let legend = sourceLegend;

    if (frameCountsByGroup && sourceLegend.legendItems) {
      const { legendItems, matchedRowCount } = applyFrameCounts(
        sourceLegend.legendItems,
        frameCountsByGroup
      );
      legend = { ...sourceLegend, legendItems };
      // Judged on every run and armed as a timer: an unchanged count keeps its
      // identity, so waiting for a second judgement would let a miss on a
      // static frame stand forever unreported. Healing always disarms it —
      // whichever side heals changes the keys, and that arrives here as a new
      // judgement. Reported once per mount, from the miss standing when the
      // timer fires.
      const isTotalMiss = legendItems.length > 0 && matchedRowCount === 0;
      if (!isTotalMiss) {
        clearStandingMiss();
      } else if (!hasReportedGroupKeyMismatchRef.current) {
        // Either side *re-keying* re-opens the healing window: new rows arrive
        // one groups round-trip ahead, and new groups re-key the next count.
        // The clock restarts on the key sets, not object identity — a pan
        // refilter hands both sides fresh objects with the same keys, and
        // restarting on those would hold a real keying bug below the threshold
        // for as long as the user composes.
        const rowKeys = JSON.stringify(
          sourceLegend.legendItems.map((item: MapLegendItem) => item.title)
        );
        const countKeys = JSON.stringify(Object.keys(frameCountsByGroup));
        const standingMiss = standingMissRef.current;
        const isNewHealingWindow =
          standingMiss !== null &&
          (standingMiss.rowKeys !== rowKeys || standingMiss.countKeys !== countKeys);
        if (isNewHealingWindow) {
          clearStandingMiss();
        }
        standingMissRef.current = { rowKeys, countKeys, legendItems, frameCountsByGroup };
        missReportTimerRef.current ??= setTimeout(() => {
          missReportTimerRef.current = null;
          const missAtTimeout = standingMissRef.current;
          if (!missAtTimeout || hasReportedGroupKeyMismatchRef.current) {
            return;
          }
          hasReportedGroupKeyMismatchRef.current = true;
          reportGroupKeyMismatch(
            entityKind,
            missAtTimeout.legendItems,
            missAtTimeout.frameCountsByGroup
          );
        }, MISMATCH_REPORT_AFTER_MS);
      }
    } else {
      // No frame counts means nothing to judge: a miss armed while composing
      // must not fire after the frame is gone.
      clearStandingMiss();
    }
    if (!legend || !legend.legendItems) return;

    const legendItems = legend.legendItems
      .filter(
        (f: MapLegendItem) =>
          f.inFilter &&
          ((placeholderBinsEnabled
            ? entityKind === EntityKind.Facility
              ? facilitiesShowGroupsNotInFilter
              : showGroupsNotInFilter
            : false) ||
            f.count > 0)
      )
      .map((item: MapLegendItem) => {
        const formattedTitle = settings?.applyAbbreviations
          ? applyAbbreviations(item.groupTitle, userAbbreviations?.abbreviations ?? [])
          : item.groupTitle;
        const legendItemModel = new LegendItemModel(formattedTitle);
        legendItemModel.inFilter = true;
        legendItemModel.color = item.color;
        legendItemModel.fontColor = "#555555";
        legendItemModel.count = item.count;
        legendItemModel.isScreenShot = true;
        return legendItemModel;
      });
    // A frame over empty ground zeroes every row and the zero-count filter
    // then drops them all; the legend silently vanishing from the image (or a
    // title over an empty body) reads as a bug, so the legend says what
    // happened instead. Frame-scoped emptiness only — rows must have existed
    // before the counts emptied them; an empty live population is Legend's own
    // message, not this one. Flipped on only after a settle window, so a
    // transient all-zero count (the tile cache swapping under a refilter)
    // cannot flash the message into a capture.
    const isFrameEmptyNow =
      Boolean(frameCountsByGroup) &&
      legendItems.length === 0 &&
      legend.legendItems.some((item: MapLegendItem) => item.inFilter);
    if (!isFrameEmptyNow) {
      if (emptyFrameTimerRef.current !== null) {
        clearTimeout(emptyFrameTimerRef.current);
        emptyFrameTimerRef.current = null;
      }
      setIsFrameEmpty(false);
    } else if (!isFrameEmpty && emptyFrameTimerRef.current === null) {
      // The `!isFrameEmpty` guard stops a frame that stays empty from arming a
      // throwaway timer on every recount just to re-set the same value.
      emptyFrameTimerRef.current = setTimeout(() => {
        emptyFrameTimerRef.current = null;
        setIsFrameEmpty(true);
      }, EMPTY_FRAME_SETTLE_MS);
    }
    setGroupLegendItems(legendItems);
  }, [
    isFrameEmpty,
    isScreenshotOverlayVisible,
    activeLegendItems,
    activeTitle,
    settings?.applyAbbreviations,
    userAbbreviations?.abbreviations,
    frameCountsByGroup,
    propertiesFilter,
    facilitiesPropertiesFilter,
    placeholderBinsEnabled,
    showGroupsNotInFilter,
    facilitiesShowGroupsNotInFilter,
    entityKind,
    clearStandingMiss
  ]);

  useEffect(updateGroupLegendItems, [updateGroupLegendItems]);

  // The timers must not outlive the legend: a report or message firing after
  // unmount would describe a frame that no longer exists.
  useEffect(
    () => () => {
      if (missReportTimerRef.current !== null) {
        clearTimeout(missReportTimerRef.current);
      }
      if (emptyFrameTimerRef.current !== null) {
        clearTimeout(emptyFrameTimerRef.current);
      }
    },
    []
  );

  const handleDragStop = (_, dragElement) => {
    const newPosition = {
      x: dragElement.x,
      y: dragElement.y
    };

    setPosition(newPosition);
    localStorage.setItem(
      positionStorageKey,
      JSON.stringify({ x: newPosition.x, y: newPosition.y })
    );
  };

  function handleResize(e, direction, ref, delta, p) {
    const nextDimensions = {
      width: ref.offsetWidth,
      height: ref.offsetHeight
    };
    setDimensions(nextDimensions);
    setPosition(p);

    localStorage.setItem(dimensionsStorageKey, JSON.stringify(nextDimensions));
    localStorage.setItem(positionStorageKey, JSON.stringify(p));
  }

  const backgroundOpacity = isScreenshotOverlayVisible
    ? entityKind === EntityKind.Facility
      ? Number(settings?.midstreamLegendOpacity ?? 1)
      : Number(settings?.upstreamLegendOpacity ?? 1)
    : 1;

  const showLegendCounts = isScreenshotOverlayVisible
    ? entityKind === EntityKind.Facility
      ? Boolean(settings?.midstreamLegendShowCounts ?? true)
      : Boolean(settings?.mapLegendShowCounts ?? true)
    : true;

  const currentLegend: LegendT = {
    ...position,
    ...dimensions
  };

  const calculatedLegend = getCalculatedLegend(currentLegend, parentDimensions);

  const storedPosition = getStoredPosition();
  const currentPosition = storedPosition
    ? { x: calculatedLegend.x, y: calculatedLegend.y }
    : getPositionFromChartSettings(
        { width: calculatedLegend.width, height: calculatedLegend.height },
        parentDimensions,
        defaultPosition
      );

  useEffect(() => {
    if (rndRef.current) {
      rndRef.current.updateOffsetFromParent();
    }
  }, [rndRef.current]);

  if (isLegendHiddenBySettings) {
    return null;
  }

  return (
    <StyledRnd
      bounds="parent"
      position={{ x: currentPosition.x, y: currentPosition.y }}
      size={{ width: calculatedLegend.width, height: calculatedLegend.height }}
      onDragStop={handleDragStop}
      onResize={handleResize}
      $mapScreenshotScale={mapScreenshotScale}
      $showLegendBorder={
        entityKind === EntityKind.Facility
          ? settings?.showMidstreamLegendBorderInScreenshot
          : settings?.showLegendBorderInScreenshot
      }
      ref={rndRef}>
      <StyledLegendWrapper
        backgroundopacity={backgroundOpacity}
        onMouseDown={(event) => {
          if (isScrollbarClicked(event)) {
            event.stopPropagation();
            event.preventDefault();
          }
        }}>
        <Legend
          className="group-map-legend"
          items={groupLegendItems}
          showTitle={true}
          showCounts={showLegendCounts}
          legendTitleSize={legendTitleSize}
          legendLabelSize={legendFontSize}
          isScreenshotOverlayVisible={isScreenshotOverlayVisible}
          entityKind={entityKind}
          legendUsage={LEGEND_USAGE_TYPES.MAP_SCREENSHOT}
          legendItemsWrapperStyle={LEGEND_WRAPPER_ITEMS_PADDING}
          showNoEntitiesMessage={isFrameEmpty}
        />
      </StyledLegendWrapper>
    </StyledRnd>
  );
};

//Styled components
const StyledRnd = styled(Rnd)`
  display: grid;
  overflow: hidden;
  min-height: ${(props: { $mapScreenshotScale: number }) =>
    props?.$mapScreenshotScale * 30 || 30}px;
  min-width: ${(props: { $mapScreenshotScale: number }) =>
    props?.$mapScreenshotScale * 150 || 150}px;
  border: ${(props: { $showLegendBorder: boolean }) =>
    props.$showLegendBorder ? "1px solid gray" : ""};

  & > div:last-child {
    position: unset;
  }

  position: relative;
  z-index: 9999; /* Set a high z-index value to ensure legend is on top within the screenshot component. */
`;

const StyledLegendWrapper = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: inline-flex;
  flex-direction: column;
  background: ${(props: { backgroundopacity: number }) =>
    props.backgroundopacity === undefined
      ? "White"
      : `rgba(255, 255, 255, ${props.backgroundopacity})`};
  overflow: hidden auto;
`;

export default LegendWrapper;
